import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  BarChart3,
  FileCheck,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Timer,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path?: string;
  children?: NavItem[];
}

const navigationItems: NavItem[] = [
  { id: 'overview', label: 'Overview', icon: BarChart3, path: '/compliance' },
  {
    id: 'frameworks',
    label: 'Frameworks',
    icon: ClipboardCheck,
    children: [
      { id: 'iso27001', label: 'ISO 27001', icon: FileCheck, path: '/compliance/requirements/iso27001' },
      { id: 'soc2', label: 'SOC 2', icon: FileCheck, path: '/compliance/requirements/soc2' },
      { id: 'smb1001', label: 'SMB 1001', icon: FileCheck, path: '/compliance/requirements/smb1001' },
    ],
  },
  { id: 'sla-settings', label: 'SLA Settings', icon: Timer, path: '/compliance/sla-settings' },
];

interface NavItemComponentProps {
  item: NavItem;
  depth?: number;
  expandedItems: Set<string>;
  toggleExpanded: (id: string) => void;
  activePath: string;
}

const NavItemComponent: React.FC<NavItemComponentProps> = ({
  item,
  depth = 0,
  expandedItems,
  toggleExpanded,
  activePath,
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.has(item.id);
  const isActive = item.path === activePath;
  const paddingLeft = 12 + depth * 16;

  const handleClick = () => {
    if (hasChildren) {
      toggleExpanded(item.id);
    }
  };

  const baseClasses = `w-full flex items-center justify-between py-2 px-3 text-sm rounded-lg transition-colors ${
    isActive
      ? 'bg-brand-primary/20 dark:bg-brand-primaryDarkMode/20 text-ui-text-primary font-medium'
      : 'text-ui-text-secondary hover:bg-ui-background-tertiary'
  }`;

  const iconClasses = isActive
    ? 'text-brand-primary dark:text-brand-primaryDarkMode'
    : '';

  const content = (
    <>
      <span className="flex items-center gap-2">
        <item.icon size={18} className={iconClasses} />
        <span>{item.label}</span>
      </span>
      {hasChildren && (
        <span className="text-ui-text-tertiary">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
      )}
    </>
  );

  return (
    <div>
      {item.path ? (
        <Link
          to={item.path}
          className={baseClasses}
          style={{ paddingLeft: `${paddingLeft}px` }}
          onClick={hasChildren ? handleClick : undefined}
        >
          {content}
        </Link>
      ) : (
        <button
          onClick={handleClick}
          className={baseClasses}
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          {content}
        </button>
      )}
      {hasChildren && isExpanded && (
        <div className="mt-1">
          {item.children!.map((child) => (
            <NavItemComponent
              key={child.id}
              item={child}
              depth={depth + 1}
              expandedItems={expandedItems}
              toggleExpanded={toggleExpanded}
              activePath={activePath}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ComplianceSidebar: React.FC = () => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(
    new Set(['frameworks'])
  );

  const toggleExpanded = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <aside className="w-64 h-full bg-ui-background-primary border-r border-ui-border-primary flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-ui-border-primary">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-ui-background-tertiary rounded-lg flex items-center justify-center">
            <ClipboardCheck className="w-5 h-5 text-ui-text-secondary" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-ui-text-primary">
              Compliance
            </h2>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {navigationItems.map((item) => (
          <NavItemComponent
            key={item.id}
            item={item}
            expandedItems={expandedItems}
            toggleExpanded={toggleExpanded}
            activePath={location.pathname}
          />
        ))}
      </nav>
    </aside>
  );
};

export default ComplianceSidebar;
